import { BufferAttribute, BufferGeometry } from 'three';
import { computeVerticesIndexArray } from '@thirdPartyLibraries/three-mesh-halfedge/src/operations/setFromGeometry';

/**
 * Drops triangles that collapse when the half-edge structure welds coincident
 * vertices, and returns how many went.
 *
 * A mesh can carry vertices at BIT-IDENTICAL positions and still look perfectly
 * healthy in a viewer. The correction's saved gum is the case that found this:
 * the designer snaps the gum seam onto the crowns, and two seam vertices landing
 * on the same crown vertex end up on the same point. Nothing breaks while that
 * mesh is live — its half-edge structure was built before the snap and is never
 * rebuilt — but re-loading the exported PLY welds the pair into one vertex, and
 * a triangle that used both corners becomes an edge from a vertex to itself.
 * `addEdge` throws on that ("Vertices v1 and v2 should be different"), and
 * `HalfEdgeMesh`'s constructor CATCHES the throw, so the failure surfaces later
 * as a half-built structure rather than as the load error it is.
 *
 * Dropping them loses no surface: a triangle with two corners on one point has
 * zero area. Measured on a captured lower jaw, 3 of 22013 faces from 5
 * coincident pairs; the matching upper jaw had none.
 *
 * Uses the structure's OWN weld (`computeVerticesIndexArray`) so the criterion
 * here cannot drift away from the one that decides whether it throws.
 *
 * Mutates `geometry`'s index in place, and only when something is dropped.
 */
export function dropCollapsedTriangles(geometry: BufferGeometry, tolerance = 1e-10): number {
  const index = geometry.getIndex();
  if (!index) return 0;

  const welded = computeVerticesIndexArray(geometry.getAttribute('position'), tolerance);
  const kept: number[] = [];
  let dropped = 0;

  for (let i = 0; i < index.count; i += 3) {
    const a = index.getX(i);
    const b = index.getX(i + 1);
    const c = index.getX(i + 2);
    if (welded[a] === welded[b] || welded[b] === welded[c] || welded[a] === welded[c]) {
      dropped += 1;
      continue;
    }
    kept.push(a, b, c);
  }

  if (dropped > 0) geometry.setIndex(new BufferAttribute(new Uint32Array(kept), 1));
  return dropped;
}
